import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, RotateCcw, X } from "lucide-react";
import { api } from "@/lib/api";
import { VehicleCard } from "@/components/VehicleCard";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { useSite, usePageSeo } from "@/context/SiteContext";
import {
  MAKES, MAKES_MODELS, TRUCK_CLASSES, CONDITIONS,
  TRANSMISSIONS, FUEL_TYPES, DRIVETRAINS, ENGINE_TYPES, YEARS,
} from "@/constants/vehicleData";

const EMPTY_FILTERS = {
  search: "", make: "", model: "", category: "", truck_class: "", condition: "",
  transmission: "", fuel_type: "", drivetrain: "", engine_type: "", color: "",
  min_year: "", max_year: "", min_price: "", max_price: "",
  min_mileage: "", max_mileage: "", min_payload: "",
};

const selectCls =
  "h-10 w-full rounded-sm border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary";
const inputCls =
  "h-10 w-full rounded-sm border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary";

const FilterSelect = ({ label, value, onChange, options, testid, placeholder = "All" }) => (
  <div>
    <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className={selectCls} data-testid={testid}>
      <option value="">{placeholder}</option>
      {options.map((o) =>
        typeof o === "object" ? (
          <option key={o.value} value={o.value}>{o.label}</option>
        ) : (
          <option key={o} value={o}>{o}</option>
        )
      )}
    </select>
  </div>
);

const FilterPanel = ({ idPrefix = "filter", filters, setF, modelOptions, categoryOptions, activeCount, onReset }) => (
    <div className="space-y-5">
      <div>
        <label htmlFor={`${idPrefix}-search`} className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">Search</label>
        <input
          id={`${idPrefix}-search`}
          value={filters.search}
          onChange={(e) => setF("search")(e.target.value)}
          placeholder="Search title, keyword..."
          className={inputCls}
          data-testid={`${idPrefix}-search-input`}
        />
      </div>
      <FilterSelect label="Make" value={filters.make} onChange={setF("make")} options={MAKES} testid={`${idPrefix}-make-select`} placeholder="All Makes" />
      <div>
        <label htmlFor={`${idPrefix}-model`} className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">Model</label>
        <select
          id={`${idPrefix}-model`}
          value={filters.model}
          onChange={(e) => setF("model")(e.target.value)}
          disabled={!filters.make}
          className={`${selectCls} disabled:opacity-50`}
          data-testid={`${idPrefix}-model-select`}
        >
          <option value="">{filters.make ? "All Models" : "Select make first"}</option>
          {modelOptions.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>
      <FilterSelect label="Category" value={filters.category} onChange={setF("category")} options={categoryOptions} testid={`${idPrefix}-type-select`} placeholder="All Categories" />
      <FilterSelect label="Truck Class" value={filters.truck_class} onChange={setF("truck_class")} options={TRUCK_CLASSES} testid={`${idPrefix}-class-select`} placeholder="All Classes" />
      <FilterSelect label="Condition" value={filters.condition} onChange={setF("condition")} options={CONDITIONS} testid={`${idPrefix}-condition-select`} placeholder="Any Condition" />
      <div className="grid grid-cols-2 gap-3">
        <FilterSelect label="Year Min" value={filters.min_year} onChange={setF("min_year")} options={YEARS.slice().reverse()} testid={`${idPrefix}-year-min`} placeholder="1990" />
        <FilterSelect label="Year Max" value={filters.max_year} onChange={setF("max_year")} options={YEARS} testid={`${idPrefix}-year-max`} placeholder="2026" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">Price Min</label>
          <input type="number" min={0} value={filters.min_price} onChange={(e) => setF("min_price")(e.target.value)} placeholder="$0" className={inputCls} data-testid={`${idPrefix}-price-min`} />
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">Price Max</label>
          <input type="number" min={0} value={filters.max_price} onChange={(e) => setF("max_price")(e.target.value)} placeholder="Any" className={inputCls} data-testid={`${idPrefix}-price-max`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">Mileage Min</label>
          <input type="number" min={0} value={filters.min_mileage} onChange={(e) => setF("min_mileage")(e.target.value)} placeholder="0" className={inputCls} data-testid={`${idPrefix}-mileage-min`} />
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">Mileage Max</label>
          <input type="number" min={0} value={filters.max_mileage} onChange={(e) => setF("max_mileage")(e.target.value)} placeholder="Any" className={inputCls} data-testid={`${idPrefix}-mileage-max`} />
        </div>
      </div>
      <FilterSelect label="Transmission" value={filters.transmission} onChange={setF("transmission")} options={TRANSMISSIONS} testid={`${idPrefix}-transmission-select`} placeholder="Any" />
      <FilterSelect label="Fuel Type" value={filters.fuel_type} onChange={setF("fuel_type")} options={FUEL_TYPES} testid={`${idPrefix}-fuel-select`} placeholder="Any" />
      <FilterSelect label="Drivetrain" value={filters.drivetrain} onChange={setF("drivetrain")} options={DRIVETRAINS} testid={`${idPrefix}-drivetrain-select`} placeholder="Any" />
      <FilterSelect label="Engine Type" value={filters.engine_type} onChange={setF("engine_type")} options={ENGINE_TYPES} testid={`${idPrefix}-engine-select`} placeholder="Any" />
      <div>
        <label htmlFor={`${idPrefix}-color`} className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">Exterior Colour</label>
        <input
          id={`${idPrefix}-color`}
          value={filters.color}
          onChange={(e) => setF("color")(e.target.value)}
          placeholder="e.g. Black, White, Red"
          className={inputCls}
          data-testid={`${idPrefix}-color-input`}
        />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-payload`} className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">Min Payload (lbs)</label>
        <input id={`${idPrefix}-payload`} type="number" min={0} value={filters.min_payload} onChange={(e) => setF("min_payload")(e.target.value)} placeholder="e.g. 5000" className={inputCls} data-testid={`${idPrefix}-payload-min`} />
      </div>
      <Button
        variant="outline"
        onClick={onReset}
        className="w-full border-input text-foreground hover:text-primary hover:border-primary rounded-sm uppercase text-xs font-bold tracking-wider bg-transparent"
        data-testid={`${idPrefix}-reset-button`}
      >
        <RotateCcw className="h-3.5 w-3.5 mr-2" /> Clear Filters {activeCount > 0 && `(${activeCount})`}
      </Button>
    </div>
);

export default function InventoryPage() {
  usePageSeo("Used Vehicles for Sale in Edmonton, AB", "Browse the current inventory at SSH Auto Sales in Edmonton, Alberta. Filter used cars, trucks and SUVs by make, model, price, mileage and more.");
  const { categories } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => {
    const f = { ...EMPTY_FILTERS };
    Object.keys(f).forEach((k) => {
      const v = searchParams.get(k);
      if (v) f[k] = v;
    });
    return f;
  });
  const [sort, setSort] = useState(searchParams.get("sort") || "featured");
  const [vehicles, setVehicles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const PAGE_SIZE = 12;

  useEffect(() => {
    setPage(1);
  }, [filters, sort]);

  useEffect(() => {
    const run = () => {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v != null) params[k] = v;
      });
      params.sort = sort;
      setSearchParams(params, { replace: true });
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      setError(false);
      api
        .get("/vehicles", { params: { ...params, skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE } })
        .then((r) => {
          setTotal(Number(r.headers["x-total-count"] || r.data.length));
          setVehicles((prev) => (page === 1 ? r.data : [...prev, ...r.data]));
        })
        .catch(() => {
          if (page === 1) setVehicles([]);
          setError(true);
        })
        .finally(() => {
          setLoading(false);
          setLoadingMore(false);
        });
    };
    if (page === 1) {
      const timer = setTimeout(run, 250);
      return () => clearTimeout(timer);
    }
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort, page, setSearchParams]);

  const setF = (k) => (v) =>
    setFilters((f) => ({ ...f, [k]: v, ...(k === "make" ? { model: "" } : {}) }));

  const activeCount = useMemo(() => Object.values(filters).filter((v) => v !== "").length, [filters]);

  const modelOptions = filters.make ? MAKES_MODELS[filters.make] || [] : [];
  const categoryOptions = categories.map((c) => ({ value: c.slug, label: c.title }));


  return (
    <div className="min-h-screen bg-background" data-testid="inventory-page">
      <div className="bg-muted border-b border-border py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight text-foreground">
            Inventory
          </h1>
          <p className="text-muted-foreground mt-2">Current available vehicles at SSH Auto Sales in Edmonton, Alberta.</p>
          <div className="flex gap-4 mt-5 text-sm font-bold uppercase tracking-wider">
            <span className="text-primary border-b-2 border-primary pb-1" data-testid="tab-available">Available</span>
            <Link to="/inventory/sold" className="text-muted-foreground hover:text-primary pb-1" data-testid="tab-sold-link">
              Recently Sold
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          <aside className="hidden lg:block w-72 shrink-0">
            <div className="sticky top-24 bg-card border border-border rounded-md p-5 max-h-[calc(100vh-120px)] overflow-y-auto scrollbar-dark" data-testid="filter-sidebar">
              <h2 className="font-display text-lg font-bold uppercase text-foreground mb-5 flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 text-primary" /> Filters
              </h2>
              <FilterPanel filters={filters} setF={setF} modelOptions={modelOptions} categoryOptions={categoryOptions} activeCount={activeCount} onReset={() => setFilters(EMPTY_FILTERS)} />
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-3">
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden border-input text-foreground rounded-sm bg-transparent" data-testid="mobile-filters-button">
                      <SlidersHorizontal className="h-4 w-4 mr-2" /> Filters {activeCount > 0 && `(${activeCount})`}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="bg-background border-border w-[320px] overflow-y-auto">
                    <SheetHeader>
                      <SheetTitle className="font-display uppercase text-foreground flex items-center justify-between">
                        Filters
                        <button onClick={() => setMobileOpen(false)} className="text-muted-foreground hover:text-foreground" aria-label="Close filters" data-testid="mobile-filters-close">
                          <X className="h-5 w-5" />
                        </button>
                      </SheetTitle>
                    </SheetHeader>
                    <div className="mt-6 pb-8">
                      <FilterPanel idPrefix="mfilter" filters={filters} setF={setF} modelOptions={modelOptions} categoryOptions={categoryOptions} activeCount={activeCount} onReset={() => setFilters(EMPTY_FILTERS)} />
                    </div>
                  </SheetContent>
                </Sheet>
                <p className="text-sm text-muted-foreground" data-testid="inventory-results-count">
                  <span className="text-primary font-bold">{total}</span> {total === 1 ? "vehicle" : "vehicles"} found
                </p>
              </div>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className={`${selectCls} w-48`} aria-label="Sort inventory" data-testid="sort-select">
                <option value="featured">Sort: Featured</option>
                <option value="newest">Sort: Newest</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="mileage_asc">Mileage: Lowest</option>
              </select>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-card border border-border rounded-md h-96 animate-pulse" />
                ))}
              </div>
            ) : error ? (
              <div className="border border-dashed border-border rounded-md p-20 text-center" data-testid="inventory-error">
                <p className="font-display text-xl font-bold uppercase text-foreground mb-2">Something Went Wrong</p>
                <p className="text-sm text-muted-foreground">We could not load inventory. Please try again in a moment.</p>
              </div>
            ) : vehicles.length === 0 ? (
              <div className="border border-dashed border-border rounded-md p-20 text-center" data-testid="inventory-empty">
                <p className="font-display text-xl font-bold uppercase text-foreground mb-2">No vehicles match your current filters.</p>
                <p className="text-sm text-muted-foreground mb-6">Try widening your search or clear the filters.</p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button
                    onClick={() => setFilters(EMPTY_FILTERS)}
                    className="bg-primary hover:bg-primary-hover text-primary-foreground font-bold uppercase rounded-sm"
                    data-testid="inventory-empty-reset"
                  >
                    Clear Filters
                  </Button>
                  <Link to="/contact" className="btn-themed-outline" data-testid="inventory-empty-contact">
                    Contact Us
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="inventory-grid">
                  {vehicles.map((v) => (
                    <VehicleCard key={v.id} vehicle={v} />
                  ))}
                </div>
                {vehicles.length < total && (
                  <div className="text-center mt-10">
                    <Button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={loadingMore}
                      variant="outline"
                      className="border-input text-foreground hover:text-primary hover:border-primary rounded-sm uppercase text-xs font-bold tracking-wider px-8 bg-transparent"
                      data-testid="load-more-button"
                    >
                      {loadingMore ? "Loading..." : `Load More (${total - vehicles.length} remaining)`}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
